import type { ProcessingJob } from "features/dt-plus-setup/model/types";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUserFromLocalStorage } from "utils/localStorage";

import type { JobLogEntry } from "../lib/parseJobLogEvent";

import { readJobLogStream } from "../lib/readJobLogStream";

export const useJobLogStream = (job: null | ProcessingJob | undefined, enabled: boolean) => {
  const { t } = useTranslation();
  const [lines, setLines] = useState<JobLogEntry[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const jobId = job?.job_id;

  useEffect(() => {
    if (!enabled || !jobId) return;

    const abortController = new AbortController();
    const user = getUserFromLocalStorage();
    const accessToken = typeof user.access_token === "string" ? user.access_token : "";
    const errorMessage = t("Execution.transient.jobDetails.logsFailed");

    setLines([]);
    setError(null);
    setIsStreaming(true);

    readJobLogStream({
      accessToken,
      errorMessage,
      jobId,
      onLine: (line) => setLines((current) => [...current, line]),
      signal: abortController.signal,
    })
      .catch((streamError: unknown) => {
        if (abortController.signal.aborted) return;
        setError(streamError instanceof Error && streamError.message ? streamError.message : errorMessage);
      })
      .finally(() => {
        if (!abortController.signal.aborted) setIsStreaming(false);
      });

    return () => abortController.abort();
  }, [enabled, jobId, t]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [lines, isStreaming]);

  return { bottomRef, error, isStreaming, lines };
};
